import { z } from 'zod';
import { addDays, isoDate, yearOf } from './dates.js';

// Statutory public holidays in Germany per federal state (GANTT-07, RES-02). Own table instead
// of a holiday library, so the frontend can use it without a large bundle (decision Phase 7a).
// Only holidays that apply statewide count, with one exception: DE-BY includes Assumption Day
// (15.8.), which applies in Munich and most Bavarian municipalities. Augsburg's Peace Festival
// and the partial Corpus Christi in Saxony and Thuringia are not included.

export const holidayRegions = {
  'DE-BW': 'Baden-Württemberg',
  'DE-BY': 'Bayern',
  'DE-BE': 'Berlin',
  'DE-BB': 'Brandenburg',
  'DE-HB': 'Bremen',
  'DE-HH': 'Hamburg',
  'DE-HE': 'Hessen',
  'DE-MV': 'Mecklenburg-Vorpommern',
  'DE-NI': 'Niedersachsen',
  'DE-NW': 'Nordrhein-Westfalen',
  'DE-RP': 'Rheinland-Pfalz',
  'DE-SL': 'Saarland',
  'DE-SN': 'Sachsen',
  'DE-ST': 'Sachsen-Anhalt',
  'DE-SH': 'Schleswig-Holstein',
  'DE-TH': 'Thüringen',
} as const;

export type HolidayRegion = keyof typeof holidayRegions;

// z.enum needs a non-empty tuple; the object above has 16 keys.
const regionKeys = Object.keys(holidayRegions) as [HolidayRegion, ...HolidayRegion[]];
export const holidayRegionSchema = z.enum(regionKeys);

export function isHolidayRegion(value: string): value is HolidayRegion {
  return Object.hasOwn(holidayRegions, value);
}

export interface Holiday {
  date: string;
  name: string;
}

/** Easter Sunday (Gregorian calendar, anonymous algorithm). */
export function easterSunday(year: number): string {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return isoDate(year, month, day);
}

/** Day of Prayer and Repentance: the Wednesday before 23 November. */
function repentanceDay(year: number): string {
  const nov22 = isoDate(year, 11, 22);
  const weekday = new Date(`${nov22}T00:00:00Z`).getUTCDay(); // 0 = Sunday, 3 = Wednesday
  return addDays(nov22, -((weekday + 4) % 7));
}

type Rule = {
  name: string;
  date: (year: number, easter: string) => string;
  /** States where the holiday applies; `all` for nationwide. */
  regions: readonly HolidayRegion[] | 'all';
  /** First year (inclusive) the holiday applies, for holidays introduced later. */
  since?: number;
  /** Only in these years (one-off holidays). */
  years?: readonly number[];
};

const fixed = (month: number, day: number) => (year: number) => isoDate(year, month, day);
const easterOffset = (days: number) => (_: number, easter: string) => addDays(easter, days);

const rules: readonly Rule[] = [
  { name: 'Neujahr', date: fixed(1, 1), regions: 'all' },
  { name: 'Heilige Drei Könige', date: fixed(1, 6), regions: ['DE-BW', 'DE-BY', 'DE-ST'] },
  { name: 'Internationaler Frauentag', date: fixed(3, 8), regions: ['DE-BE'], since: 2019 },
  { name: 'Internationaler Frauentag', date: fixed(3, 8), regions: ['DE-MV'], since: 2023 },
  { name: 'Karfreitag', date: easterOffset(-2), regions: 'all' },
  { name: 'Ostersonntag', date: easterOffset(0), regions: ['DE-BB'] },
  { name: 'Ostermontag', date: easterOffset(1), regions: 'all' },
  { name: 'Tag der Arbeit', date: fixed(5, 1), regions: 'all' },
  {
    name: 'Tag der Befreiung',
    date: fixed(5, 8),
    regions: ['DE-BE'],
    years: [2020, 2025],
  },
  { name: 'Christi Himmelfahrt', date: easterOffset(39), regions: 'all' },
  {
    name: 'Jahrestag des Volksaufstands vom 17. Juni 1953',
    date: fixed(6, 17),
    regions: ['DE-BE'],
    years: [2028],
  },
  { name: 'Pfingstsonntag', date: easterOffset(49), regions: ['DE-BB'] },
  { name: 'Pfingstmontag', date: easterOffset(50), regions: 'all' },
  {
    name: 'Fronleichnam',
    date: easterOffset(60),
    regions: ['DE-BW', 'DE-BY', 'DE-HE', 'DE-NW', 'DE-RP', 'DE-SL'],
  },
  { name: 'Mariä Himmelfahrt', date: fixed(8, 15), regions: ['DE-BY', 'DE-SL'] },
  { name: 'Weltkindertag', date: fixed(9, 20), regions: ['DE-TH'], since: 2019 },
  { name: 'Tag der Deutschen Einheit', date: fixed(10, 3), regions: 'all' },
  {
    name: 'Reformationstag',
    date: fixed(10, 31),
    regions: ['DE-BB', 'DE-MV', 'DE-SN', 'DE-ST', 'DE-TH'],
  },
  {
    name: 'Reformationstag',
    date: fixed(10, 31),
    regions: ['DE-HB', 'DE-HH', 'DE-NI', 'DE-SH'],
    since: 2018,
  },
  {
    name: 'Reformationstag',
    date: fixed(10, 31),
    regions: ['DE-BW', 'DE-BY', 'DE-BE', 'DE-HE', 'DE-NW', 'DE-RP', 'DE-SL'],
    years: [2017],
  },
  {
    name: 'Allerheiligen',
    date: fixed(11, 1),
    regions: ['DE-BW', 'DE-BY', 'DE-NW', 'DE-RP', 'DE-SL'],
  },
  { name: 'Buß- und Bettag', date: repentanceDay, regions: ['DE-SN'] },
  { name: '1. Weihnachtstag', date: fixed(12, 25), regions: 'all' },
  { name: '2. Weihnachtstag', date: fixed(12, 26), regions: 'all' },
];

const cache = new Map<string, readonly Holiday[]>();

/** Public holidays of a region in a year, sorted by date. */
export function holidaysOf(region: HolidayRegion, year: number): readonly Holiday[] {
  const key = `${region}:${year}`;
  const cached = cache.get(key);
  if (cached) return cached;
  const easter = easterSunday(year);
  const holidays = rules
    .filter(
      (rule) =>
        (rule.regions === 'all' || rule.regions.includes(region)) &&
        (rule.since === undefined || year >= rule.since) &&
        (rule.years === undefined || rule.years.includes(year)),
    )
    .map((rule) => ({ date: rule.date(year, easter), name: rule.name }))
    .sort((a, b) => a.date.localeCompare(b.date));
  cache.set(key, holidays);
  return holidays;
}

/** The holiday on `date` in `region`, or null. */
export function holidayOn(date: string, region: HolidayRegion): Holiday | null {
  return holidaysOf(region, yearOf(date)).find((h) => h.date === date) ?? null;
}
